
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * InvoiceBatchItem
 *
 * A per-invoice row within an invoice batch, capturing a financial snapshot of one finalized invoice as of the batch date. Stores billing totals (labor and expenses), trust accounting state (current balance, minimum required, replenishment flags), running balance figures (previous balance, amount added, amount due), payment breakdowns (direct and trust payments), and the amounts to transfer from trust to the operating account for that invoice.
 */
class InvoiceBatchItem extends Base implements EntityType {
    invoiceBatchItemId: uuid;
    invoiceBatchId?: uuid | null;
    invoiceId?: uuid | null;
    invoiceNumber?: string | null;
    endDate?: Date | null;
    clientNames?: string | null;
    matterName?: string | null;
    sentDate?: Date | null;
    sentUsing?: string | null;
    expenseAmount?: number | null;
    earnedAmount?: number | null;
    minimumCaseTrust?: number | null;
    currentTrustBalance?: number | null;
    requiredTrustPayment?: number | null;
    replenishmentRequired: boolean;
    replenishmentDone: boolean;
    previousBalance?: number | null;
    addedBalance?: number | null;
    amountDue?: number | null;
    directPayments?: number | null;
    trustPayments?: number | null;
    expensesTransferAmount?: number | null;
    laborTransferAmount?: number | null;
    notes?: string | null;

    /**
     * Create a new InvoiceBatchItem, with an (optional) JSON object or InvoiceBatchItem class
     *
     * @param {Object|InvoiceBatchItem} doc - Can be either a simple JSON object or a InvoiceBatchItem class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof InvoiceBatchItem | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            invoiceBatchItemId: genUUID(),
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'invoiceBatchItem';
        // @ts-ignore
        this.invoiceBatchItemId = doc._invoiceBatchItemId || doc.invoiceBatchItemId || doc.invoicebatchitemid || genUUID();
        // @ts-ignore
        this.invoiceBatchId = doc._invoiceBatchId || doc.invoiceBatchId || doc.invoicebatchid || null;
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
        // @ts-ignore
        this.invoiceNumber = doc._invoiceNumber || doc.invoiceNumber || doc.invoicenumber || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.clientNames = doc._clientNames || doc.clientNames || doc.clientnames || null;
        // @ts-ignore
        this.matterName = doc._matterName || doc.matterName || doc.mattername || null;
        // @ts-ignore
        this.sentDate = DateUtil.dateOnly(doc._sentDate) || DateUtil.dateOnly(doc.sentDate) || DateUtil.dateOnly(doc.sentdate) || null;
        // @ts-ignore
        this.sentUsing = doc._sentUsing || doc.sentUsing || doc.sentusing || null;
        // @ts-ignore
        this.expenseAmount = util.coalesce(util.parseFloat(doc._expenseAmount), util.parseFloat(doc.expenseAmount), util.parseFloat(doc.expenseamount), null);
        // @ts-ignore
        this.earnedAmount = util.coalesce(util.parseFloat(doc._earnedAmount), util.parseFloat(doc.earnedAmount), util.parseFloat(doc.earnedamount), null);
        // @ts-ignore
        this.minimumCaseTrust = util.coalesce(util.parseFloat(doc._minimumCaseTrust), util.parseFloat(doc.minimumCaseTrust), util.parseFloat(doc.minimumcasetrust), null);
        // @ts-ignore
        this.currentTrustBalance = util.coalesce(util.parseFloat(doc._currentTrustBalance), util.parseFloat(doc.currentTrustBalance), util.parseFloat(doc.currenttrustbalance), null);
        // @ts-ignore
        this.requiredTrustPayment = util.coalesce(util.parseFloat(doc._requiredTrustPayment), util.parseFloat(doc.requiredTrustPayment), util.parseFloat(doc.requiredtrustpayment), null);
        // @ts-ignore
        this.replenishmentRequired = util.defaultBoolean([doc.replenishmentRequired, doc._replenishmentRequired, doc.replenishmentrequired, false]);
        // @ts-ignore
        this.replenishmentDone = util.defaultBoolean([doc.replenishmentDone, doc._replenishmentDone, doc.replenishmentdone, false]);
        // @ts-ignore
        this.previousBalance = util.coalesce(util.parseFloat(doc._previousBalance), util.parseFloat(doc.previousBalance), util.parseFloat(doc.previousbalance), null);
        // @ts-ignore
        this.addedBalance = util.coalesce(util.parseFloat(doc._addedBalance), util.parseFloat(doc.addedBalance), util.parseFloat(doc.addedbalance), null);
        // @ts-ignore
        this.amountDue = util.coalesce(util.parseFloat(doc._amountDue), util.parseFloat(doc.amountDue), util.parseFloat(doc.amountdue), null);
        // @ts-ignore
        this.directPayments = util.coalesce(util.parseFloat(doc._directPayments), util.parseFloat(doc.directPayments), util.parseFloat(doc.directpayments), null);
        // @ts-ignore
        this.trustPayments = util.coalesce(util.parseFloat(doc._trustPayments), util.parseFloat(doc.trustPayments), util.parseFloat(doc.trustpayments), null);
        // @ts-ignore
        this.expensesTransferAmount = util.coalesce(util.parseFloat(doc._expensesTransferAmount), util.parseFloat(doc.expensesTransferAmount), util.parseFloat(doc.expensestransferamount), null);
        // @ts-ignore
        this.laborTransferAmount = util.coalesce(util.parseFloat(doc._laborTransferAmount), util.parseFloat(doc.laborTransferAmount), util.parseFloat(doc.labortransferamount), null);
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the invoiceBatchItemId across all entity types
     */
    get id(): uuid {
        return this.invoiceBatchItemId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this invoiceBatchItem"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.invoiceBatchItemId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied InvoiceBatchItem entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): InvoiceBatchItem[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'invoiceBatchItem',
            table: 'INVOICE_BATCH_ITEM',
            comment: 'A per-invoice row within an invoice batch, capturing a financial snapshot of one finalized invoice as of the batch date. Stores billing totals (labor and expenses), trust accounting state (current balance, minimum required, replenishment flags), running balance figures (previous balance, amount added, amount due), payment breakdowns (direct and trust payments), and the amounts to transfer from trust to the operating account for that invoice.',
            fields: {
                invoiceBatchItemId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this invoiceBatchItem'
                },
                invoiceBatchId: {
                    type: 'uuid',
                    comment: 'Associated invoiceBatch'
                },
                invoiceId: {
                    type: 'uuid',
                    entityType: 'invoice',
                    comment: 'Related invoice'
                },
                invoiceNumber: {
                    type: 'string',
                    comment: 'Invoice #'
                },
                endDate: {
                    type: 'date',
                    comment: 'Last day of the invoice period'
                },
                clientNames: {
                    type: 'string',
                    comment: 'Names of the clients'
                },
                matterName: {
                    type: 'string',
                    comment: 'Name of the matter'
                },
                sentDate: {
                    type: 'date',
                    comment: 'Date the invoice was sent to the client'
                },
                sentUsing: {
                    type: 'string',
                    comment: 'How was the invoice sent?'
                },
                expenseAmount: {
                    type: 'money',
                    comment: 'Net billed expense amount for this invoice (expenses minus expense discounts)'
                },
                earnedAmount: {
                    type: 'money',
                    comment: 'Net billed labor amount for this invoice (tasks plus earned flat fees, minus labor discounts)'
                },
                minimumCaseTrust: {
                    type: 'money',
                    comment: 'If there is a minimum case trust balance, it goes here'
                },
                currentTrustBalance: {
                    type: 'money',
                    comment: 'Amount of trust available over all trust types'
                },
                requiredTrustPayment: {
                    type: 'money',
                    comment: 'Amount of funds needed to top off trust'
                },
                replenishmentRequired: {
                    type: 'boolean',
                    comment: 'Is a replenishment required? Defaults to required trust payment > 0'
                },
                replenishmentDone: {
                    type: 'boolean',
                    comment: 'Has the replenishment happened?'
                },
                previousBalance: {
                    type: 'money',
                    comment: 'Amount due from previous invoice'
                },
                addedBalance: {
                    type: 'money',
                    comment: 'Amount added to the balance this invoice'
                },
                amountDue: {
                    type: 'money',
                    comment: 'New amount due (previous + added balances)'
                },
                directPayments: {
                    type: 'money',
                    comment: 'Direct payments made'
                },
                trustPayments: {
                    type: 'money',
                    comment: 'Payments from trust'
                },
                expensesTransferAmount: {
                    type: 'money',
                    comment: 'Amount to transfer for expenses'
                },
                laborTransferAmount: {
                    type: 'money',
                    comment: 'Amount to transfer for labor'
                },
                notes: {
                    type: 'string',
                    comment: 'Any notes or comments'
                }
            },
            indexes: [{
                name: 'notDeleted',
                fn: a => a.status !== 'D'
            }],
            maps: [{
                    name: 'invoiceId',
                    keysFn: t => t.invoiceId,
                    presentFn: (t, k) => t.invoiceId === k
                },
                {
                    name: 'invoiceBatchId',
                    keysFn: t => t.invoiceBatchId,
                    presentFn: (t, k) => t.invoiceBatchId === k
                },
            ]
        });
    }

}
export default InvoiceBatchItem;
